import { getCLS, getFID, getFCP, getLCP, getTTFB, Metric } from 'web-vitals';

// Types for Web Vitals metrics
interface WebVitalMetric extends Metric {
  rating: 'good' | 'needs-improvement' | 'poor';
}

// Thresholds for Core Web Vitals
const VITALS_THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 },
  FID: { good: 100, poor: 300 },
  CLS: { good: 0.1, poor: 0.25 },
  FCP: { good: 1800, poor: 3000 },
  TTFB: { good: 800, poor: 1800 }
};

// Get rating based on thresholds
const getRating = (name: string, value: number): 'good' | 'needs-improvement' | 'poor' => {
  const threshold = VITALS_THRESHOLDS[name as keyof typeof VITALS_THRESHOLDS];
  if (!threshold) return 'good';
  
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
};

// Send vitals to analytics
const sendToAnalytics = (metric: WebVitalMetric) => {
  // Send to Google Analytics 4
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', metric.name, {
      custom_parameter_1: metric.value,
      custom_parameter_2: metric.rating,
      custom_parameter_3: metric.id,
    });
  }

  // Send to console in development
  if (process.env.NODE_ENV === 'development') {
    console.group(`🔍 Web Vital: ${metric.name}`);
    console.log(`Value: ${metric.value}ms`);
    console.log(`Rating: ${metric.rating}`);
    console.log(`ID: ${metric.id}`);
    console.groupEnd();
  }

  // Send to performance monitoring service (can be customized)
  if (typeof window !== 'undefined') {
    // Store in localStorage for debugging
    const vitals = JSON.parse(localStorage.getItem('webVitals') || '[]');
    vitals.push({
      name: metric.name,
      value: metric.value,
      rating: metric.rating,
      timestamp: Date.now(),
      url: window.location.pathname
    });
    localStorage.setItem('webVitals', JSON.stringify(vitals.slice(-50))); // Keep last 50 measurements
  }
};

// Enhanced metric handler
const handleMetric = (metric: Metric) => {
  const enhancedMetric: WebVitalMetric = {
    ...metric,
    rating: getRating(metric.name, metric.value)
  };
  
  sendToAnalytics(enhancedMetric);
};

// Initialize Web Vitals monitoring
export const initWebVitals = () => {
  if (typeof window === 'undefined') return;

  // Core Web Vitals
  getLCP(handleMetric);
  getFID(handleMetric);
  getCLS(handleMetric);
  
  // Additional metrics
  getFCP(handleMetric);
  getTTFB(handleMetric);
};

// Get current vitals (useful for debugging)
export const getCurrentVitals = async () => {
  return new Promise((resolve) => {
    const vitals: any = {};
    
    getCLS((metric) => {
      vitals.CLS = { ...metric, rating: getRating('CLS', metric.value) };
    });
    
    getFID((metric) => {
      vitals.FID = { ...metric, rating: getRating('FID', metric.value) };
    });
    
    getFCP((metric) => {
      vitals.FCP = { ...metric, rating: getRating('FCP', metric.value) };
    });
    
    getLCP((metric) => {
      vitals.LCP = { ...metric, rating: getRating('LCP', metric.value) };
    });
    
    getTTFB((metric) => {
      vitals.TTFB = { ...metric, rating: getRating('TTFB', metric.value) };
    });
    
    // Wait a bit for metrics to be captured
    setTimeout(() => resolve(vitals), 1000);
  });
};

// Performance optimization utilities
export const performanceUtils = {
  // Preload critical resources
  preloadResource: (href: string, as: string, type?: string) => {
    if (typeof window === 'undefined') return;
    
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = href;
    link.as = as;
    if (type) link.type = type;
    document.head.appendChild(link);
  },

  // Prefetch next page resources
  prefetchResource: (href: string) => {
    if (typeof window === 'undefined') return;
    
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = href;
    document.head.appendChild(link);
  },

  // Mark resource as critical
  markCritical: (selector: string) => {
    if (typeof window === 'undefined') return;
    
    const elements = document.querySelectorAll(selector);
    elements.forEach((el) => {
      if (el instanceof HTMLImageElement) {
        el.loading = 'eager';
        // fetchPriority is not widely supported yet, so we'll skip it
        // el.fetchPriority = 'high';
      }
    });
  }
};

export default initWebVitals; 